import Button from 'ui/mobile-ds/Button';
import TicketCreatedSuccess from '@/assets/ticket_created_success.svg';
import { motion } from 'framer-motion';
import { Layout } from '@/components/Common/Layout';
import clsx from 'clsx';
import { IClaimCreateStepProps } from '@/pages/create';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppConfig from '@/AppConfig';
import { ArrowForward } from 'ui/mobile-ds/icons';
import { createPlumClaim } from '@/queries/claims.queries';
import { FailedCreation } from 'ui/icons';
import useClaimCreateStore, {
  EClaimCreateFlowSteps,
} from '@/store/claimCreateStore';
import { getRequestCallbackStorageKey } from '@/context';
import { EClaimType } from '@/types';
import { useAppContext } from '@/context/AppContext';
import useMobileBackPress from '@/hooks/useMobileBackPress';
import getClaimFieldTooLongMessage from '@/utils/getClaimFieldTooLongMessage';

const LOADER_PROPS_MAP = {
  [EClaimType.CASHLESS]: {
    title: 'Hold tight, we’re submitting your claim',
    description: 'This will take only a moment, please keep this page open',
  },
  [EClaimType.PLANNED]: {
    title: 'Hold tight, we’re submitting your claim',
    description: 'This will take only a moment, please keep this page open',
  },
  [EClaimType.REIMBURSEMENT]: {
    title: 'Please wait, we’re processing your details',
    description: 'Shouldn’t be long. Please keep this page open.',
  },
};

const ClaimCreated = ({
  createClaimPayload,
  trackCreation,
}: IClaimCreateStepProps) => {
  const setCurrentStep = useClaimCreateStore((state) => state.setCurrentStep);

  const navigate = useNavigate();

  const { data, error, isError, isPending, mutate } = useMutation({
    mutationFn: createPlumClaim().mutationFn,
    retry: false,
    onSuccess: (data) => {
      trackCreation?.(data, { creationMode: 'create' });
      if (createClaimPayload?.typeOfClaim === EClaimType.REIMBURSEMENT) {
        navigate(`${AppConfig.BASE_PATH}/claims/${data?.plumClaimId}/upload`);
      }
    },
  });

  // WEB-04: resending the same payload can never succeed when the server
  // rejected a field as too long, so send the member back to edit it instead.
  const fieldTooLongMessage = isError
    ? getClaimFieldTooLongMessage(error)
    : null;

  const { isWebView } = useAppContext();

  useMobileBackPress({
    onBackPress: () => {
      window.NativeBridge?.onNativeClose?.();
    },
    isWebView,
  });

  useEffect(() => {
    /**
     * Did this workaround for Strict Mode in dev wherein the effect runs twice,
     * but react query doesn't work (even after using refs to ensure the effect doesn't run again)
     * It's an existing bug at this point in react-query
     * refer: https://github.com/TanStack/query/issues/5341
     */
    if (createClaimPayload) {
      const timerId = setTimeout(() => {
        mutate(createClaimPayload);
      }, 0);
      return () => {
        clearTimeout(timerId);
      };
    }
  }, [createClaimPayload, mutate]);

  return (
    <Layout.Main
      loading={
        (isPending ||
          createClaimPayload?.typeOfClaim === EClaimType.REIMBURSEMENT) &&
        !isError
      }
      hasError={isError}
      loaderProps={
        LOADER_PROPS_MAP?.[
          createClaimPayload?.typeOfClaim ?? EClaimType.CASHLESS
        ] ?? {}
      }
      headerProps={{
        hideBack: isError ? false : true,
      }}
      containerProps={{
        className: 'h-full',
      }}
      fullWidth
    >
      {isError ? (
        <div className="mx-auto flex flex-col text-center items-center justify-center h-[80%] gap-4 max-w-[340px] w-full">
          <FailedCreation className="w-16 h-16" />
          <div className="flex flex-col gap-2">
            <h1 className="text-mobile-text-main font-black leading-[34px] text-[22px] font-chromatica text-center">
              Failed to create your claim
            </h1>
            <p className="text-mobile-text-hierarchy-2 text-sm leading-[20px] text-center">
              {fieldTooLongMessage ??
                'Don’t worry, you can try and submit your claim again'}
            </p>
          </div>
          {fieldTooLongMessage ? null : (
            <Button
              className="text-base max-w-[216px] w-full font-semibold"
              onClick={() => {
                mutate(createClaimPayload);
              }}
            >
              Try again
            </Button>
          )}
          <Button
            className="text-base max-w-[216px] w-full font-semibold border-primary-500 border"
            intent="tertiary"
            onClick={() => {
              if (createClaimPayload?.typeOfClaim === EClaimType.CASHLESS) {
                setCurrentStep(EClaimCreateFlowSteps.COLLECT_BASIC_INFO);
              } else if (
                createClaimPayload?.typeOfClaim === EClaimType.REIMBURSEMENT
              ) {
                setCurrentStep(EClaimCreateFlowSteps.TREATMENT_DETAILS);
              } else {
                setCurrentStep(EClaimCreateFlowSteps.SELECT_CLAIM_TYPE);
              }
            }}
          >
            Enter details again
          </Button>
        </div>
      ) : createClaimPayload?.typeOfClaim ===
        EClaimType.REIMBURSEMENT ? null : (
        <motion.div
          initial={{
            opacity: 0,
            y: 50,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
            ease: 'easeOut',
          }}
          className={clsx({
            'mx-auto flex flex-col text-center items-center justify-center h-[80%] w-full max-w-[350px]':
              true,
          })}
        >
          <img
            src={TicketCreatedSuccess}
            alt="claim_created_successfully"
            height={200}
            width={160}
          />
          <p className="mt-8 text-mobile-gray-400 text-lg font-medium leading-[120%]">
            Plum reference ID
          </p>
          <h1 className="text-mobile-gray-600 mt-1 font-black leading-[120%] text-[32px] font-chromatica">
            {data?.plumClaimId}
          </h1>
          <p className="text-sm mt-4 text-mobile-gray-500 leading-[160%]">
            You&apos;ll be able to track your progress and learn about next
            steps from the following page
          </p>
          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.7,
              delay: 0.5,
              ease: 'easeOut',
            }}
          >
            <Link to={`${AppConfig.BASE_PATH}/claims/${data?.plumClaimId}`}>
              <Button
                className="mt-8 w-[200px] text-base"
                trailingIcon={() => <ArrowForward color="white" />}
                onClick={() => {
                  if (createClaimPayload?.typeOfClaim === EClaimType.CASHLESS) {
                    sessionStorage.setItem(
                      getRequestCallbackStorageKey(data?.plumClaimId ?? '') ??
                        '',
                      true.toString(),
                    );
                  }
                }}
              >
                Track my claim
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      )}
    </Layout.Main>
  );
};

export default ClaimCreated;
